import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { AttachmentPayload } from '@/api/uploads';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer Sale Process / Journey — the sell-side pipeline that carries a lead
 * from first contact through to a signed contract. Endpoints/field names are the
 * v4 backend verbatim (`/customers/:id/customer-journey*` and, for the deal
 * closing flow, `/contracts/negotiations*`). Shapes verified against live
 * staging: the `customer_journey_explaination` (deal info) and
 * `customer_journey_notes` come back EMBEDDED on the journey row — the standalone
 * `/explaination` GET is 404 (PUT-only for updates).
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

/** Journey pipeline steps in order. */
export type JourneyStep =
  | 'searching'
  | 'presentation'
  | 'poc'
  | 'contract'
  | 'close_win'
  | 'close_lose';

export const JOURNEY_STEP_ORDER: JourneyStep[] = [
  'searching',
  'presentation',
  'poc',
  'contract',
  'close_win',
];

/* ── Types ─────────────────────────────────────────────────────────────── */

/** Embedded deal-explanation block on a journey (the "why/what" of the deal). */
export interface JourneyExplanation {
  explaination_id?: number;
  customer_id?: number;
  customer_journey_id?: number;
  provider_explaination?: string | null;
  /** Budget amount — the backend stores/accepts a number. */
  budget_explaination?: number | string | null;
  influencer_explaination?: string | null;
  problem_explaination?: string | null;
  expected_explaination?: string | null;
  explaination_result?: string | null;
  expected_date?: string | null;
  failed_reason?: string;
  failed_explanation?: string;
  failed_improvement?: string;
  /** Sale-owner employee ids driving the deal. */
  sale_tracking_id?: number[];
  service_interestings?: string[];
  explaination_created_at?: string;
  explaination_updated_at?: string;
  explaination_completed_at?: string | null;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

/** A trial (POC) service embedded on a `reference_type: 'service-poc'` note. */
export interface JourneyServicePoc {
  service_id?: number | string;
  service_name?: string;
  service_status?: string;
  service_start_date?: string | null;
  service_stopped_date?: string | null;
  product_group_name?: string;
}

/** A contracted service embedded on a `reference_type: 'service'` note. */
export interface JourneyServiceRef {
  service_id?: number | string;
  service_name?: string;
  service_status?: string;
  service_start_date?: string | null;
  service_stopped_date?: string | null;
  service_type?: string;
  product_group_name?: string;
  contract_id?: number | null;
}

/** A linked business trip embedded on a `reference_type: 'business'` note. */
export interface JourneyBusinessRef {
  business_id?: number;
  business_name?: string;
  business_status?: string;
  business_start_date?: string | null;
  business_end_date?: string | null;
}

/** A linked meeting ticket embedded on a `reference_type: 'ticket'` note. */
export interface JourneyTicketRef {
  ticket_id?: number;
  ticket_title?: string;
  ticket_status?: string;
}

/** A journey note bucketed under a step. */
export interface JourneyNote {
  customer_journey_note_id?: number;
  customer_journey_id?: number;
  customer_journey_note?: string;
  customer_journey_note_step?: string;
  customer_journey_note_step_status?: string;
  /** Backend timestamp for the note (used for grouping + display). */
  customer_journey_notes_time?: string;
  customer_journey_note_created_at?: string;
  customer_journey_note_updated_at?: string;
  /** Author is an embedded employee object on the note row. */
  employee?: { employee_id?: number; employee_name?: string } | null;
  employee_name?: string;
  /**
   * When a note is a system-linked activity, `reference_type` tags what the note
   * points at and the matching object is embedded alongside it (verified live):
   * `service-poc`→`service_poc`, `service`→`service`, `business`→`business`,
   * `ticket`→`ticket`. Plain follow-up notes have a null reference.
   */
  reference_type?: string | null;
  reference_id?: number | null;
  service_poc?: JourneyServicePoc | null;
  service?: JourneyServiceRef | null;
  business?: JourneyBusinessRef | null;
  ticket?: JourneyTicketRef | null;
  attachment?: { attachment_file_url?: string; attachment_file_name?: string } | null;
  [key: string]: unknown;
}

/** A named sale follower embedded on a journey. */
export interface JourneySaleFollower {
  employee_id?: number;
  employee_name?: string;
  [key: string]: unknown;
}

/** `GET /customers/:id/customer-journey` row (explaination + notes embedded). */
export interface CustomerJourney {
  customer_journey_id: number;
  customer_id: number;
  customer_journey_step: JourneyStep | string;
  customer_journey_step_status?: string;
  service_referrer_id?: number;
  contact_id?: number;
  customer_journey_locked?: boolean;
  customer_journey_locked_at?: string | null;
  archived?: boolean;
  archived_at?: string | null;
  archived_by?: number | null;
  deleted_at?: string | null;
  follow_up_note?: string | null;
  customer_journey_created_at?: string;
  customer_journey_updated_at?: string;
  customer_journey_explaination?: JourneyExplanation | null;
  customer_journey_notes?: JourneyNote[];
  employee_sale_followers?: JourneySaleFollower[];
}

/**
 * `GET /contracts/negotiations/customers/journeys/:jid` row. The v4 backend
 * prefixes every field with `contract_negotiation_`; the primary key is
 * `contract_negotiation_id` and the state field is `contract_negotiation_status`.
 */
export interface JourneyNegotiation {
  contract_negotiation_id?: number;
  contract_negotiation_customer_journey_id?: number;
  contract_negotiation_customer_id?: number;
  contract_negotiation_title?: string;
  contract_negotiation_code?: string;
  contract_negotiation_status?: string;
  contract_negotiation_type?: string;
  contract_negotiation_priority?: string;
  contract_negotiation_stage?: string;
  contract_negotiation_value?: number;
  contract_negotiation_progress?: number;
  contract_negotiation_content?: string;
  contract_negotiation_notes?: string;
  contract_negotiation_deadline?: string;
  /** Present once converted; links the resulting contract. */
  contract_id?: number | null;
  archived?: boolean;
  assignee_names?: string[];
  creator_name?: string;
  attachments?: Array<Record<string, unknown>>;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** `GET /contracts/negotiations/:id/histories` row. */
export interface NegotiationHistory {
  contract_negotiation_history_id?: number;
  contract_negotiation_id?: number;
  history_status?: string;
  history_note?: string;
  history_value?: number;
  employee_name?: string;
  created_at?: string;
  [key: string]: unknown;
}

/* ── Params / query keys ───────────────────────────────────────────────── */

export interface JourneyListParams {
  page?: number;
  page_size?: number;
  customer_journey_step?: string;
}

export const journeyKeys = {
  all: (customerId: number) => ['customers', customerId, 'journeys'] as const,
  list: (customerId: number, params: JourneyListParams) => ['customers', customerId, 'journeys', params] as const,
  detail: (customerId: number, journeyId: number) => ['customers', customerId, 'journeys', journeyId] as const,
  notes: (customerId: number, journeyId: number, step?: string) => ['customers', customerId, 'journeys', journeyId, 'notes', step] as const,
  negotiations: (journeyId: number) => ['contracts', 'negotiations', 'journey', journeyId] as const,
  negotiationHistories: (negotiationId: number) => ['contracts', 'negotiations', negotiationId, 'histories'] as const,
};

/* ── Journey queries ───────────────────────────────────────────────────── */

/** `GET /customers/:id/customer-journey` — all journeys (pipelines) for a customer. */
export function useCustomerJourneys(customerId: number, params: JourneyListParams = {}) {
  return useQuery({
    queryKey: journeyKeys.list(customerId, params),
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CustomerJourney>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerJourney>>(
        `/customers/${customerId}/customer-journey?${qs.stringify({ page: 1, page_size: 50, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/customer-journey/:jid` — one journey record. */
export function useCustomerJourneyDetail(customerId: number, journeyId: number) {
  return useQuery({
    queryKey: journeyKeys.detail(customerId, journeyId),
    enabled: !!customerId && !!journeyId,
    queryFn: async (): Promise<ResponseObject<CustomerJourney>> => {
      const res = await dataCentralApi.get<ResponseObject<CustomerJourney>>(
        `/customers/${customerId}/customer-journey/${journeyId}`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/customer-journey/:jid/notes` — notes, optionally by step. */
export function useJourneyNotes(customerId: number, journeyId: number, step?: string) {
  return useQuery({
    queryKey: journeyKeys.notes(customerId, journeyId, step),
    enabled: !!customerId && !!journeyId,
    queryFn: async (): Promise<ResponseArray<JourneyNote>> => {
      const res = await dataCentralApi.get<ResponseArray<JourneyNote>>(
        `/customers/${customerId}/customer-journey/${journeyId}/notes?${qs.stringify({
          page: 1,
          page_size: 100,
          // The notes endpoint filters by `step`, not the row's step field name.
          ...(step ? { step } : {}),
        })}`,
      );
      return res.data;
    },
  });
}

/* ── Journey mutations ─────────────────────────────────────────────────── */

/** `POST /customers/:id/customer-journey` — create a new journey/upsell. */
export function useCreateJourney(customerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>): Promise<ResponseObject<CustomerJourney>> => {
      const res = await dataCentralApi.post<ResponseObject<CustomerJourney>>(
        `/customers/${customerId}/customer-journey`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/** `PUT /customers/:id/customer-journey/:jid/explaination` — update deal info. */
export function useUpdateJourneyExplanation(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<JourneyExplanation>): Promise<ResponseObject<JourneyExplanation>> => {
      const res = await dataCentralApi.put<ResponseObject<JourneyExplanation>>(
        `/customers/${customerId}/customer-journey/${journeyId}/explaination`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/**
 * `POST /customers/:id/customer-journey/:jid/note` — add a journey note.
 * The backend expects the note wrapped in a `note` object (optionally with an
 * `attachment`), with the step under `customer_journey_note_step`.
 */
export interface AddJourneyNoteInput {
  customer_journey_note: string;
  customer_journey_note_step?: string;
  customer_journey_note_step_status?: string;
  customer_journey_notes_time?: string;
}

export function useAddJourneyNote(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (note: AddJourneyNoteInput): Promise<ResponseObject<JourneyNote>> => {
      const res = await dataCentralApi.post<ResponseObject<JourneyNote>>(
        `/customers/${customerId}/customer-journey/${journeyId}/note`,
        { note },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/** `PUT /customers/:id/customer-journey/:jid/step` — advance the pipeline step. */
export function useAdvanceJourneyStep(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { customer_journey_step: string; [key: string]: unknown }): Promise<ResponseObject<CustomerJourney>> => {
      const res = await dataCentralApi.put<ResponseObject<CustomerJourney>>(
        `/customers/${customerId}/customer-journey/${journeyId}/step`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/** `POST /customers/:id/customer-journey/:jid/lost` — mark the journey lost. */
export function useMarkJourneyLost(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reason: string }): Promise<ResponseObject<CustomerJourney>> => {
      const res = await dataCentralApi.post<ResponseObject<CustomerJourney>>(
        `/customers/${customerId}/customer-journey/${journeyId}/lost`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/** `POST /customers/:id/customer-journey/:jid/re-engage` — re-open a lost journey. */
export function useReengageJourney(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { note: string }): Promise<ResponseObject<CustomerJourney>> => {
      const res = await dataCentralApi.post<ResponseObject<CustomerJourney>>(
        `/customers/${customerId}/customer-journey/${journeyId}/re-engage`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/** `DELETE /customers/:id/customer-journey/:jid/note/:nid` — remove a journey note. */
export function useDeleteJourneyNote(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: number): Promise<unknown> => {
      const res = await dataCentralApi.delete(
        `/customers/${customerId}/customer-journey/${journeyId}/note/${noteId}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/**
 * `POST /customers/:id/services/pocs/:sid/convert-official` — promote a trial POC
 * service into an official (billable) service. Legacy sends no body.
 */
export function useConvertPocToOfficial(customerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serviceId: number): Promise<unknown> => {
      const res = await dataCentralApi.post(
        `/customers/${customerId}/services/pocs/${serviceId}/convert-official`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/**
 * `DELETE /customers/:id/customer-journey/:jid` — delete a journey. A reason is
 * required and travels in the request body (`{ data: { reason } }`, like legacy).
 */
export function useDeleteJourney(customerId: number, journeyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reason: string): Promise<unknown> => {
      const res = await dataCentralApi.delete(
        `/customers/${customerId}/customer-journey/${journeyId}`,
        { data: { reason } },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.all(customerId) });
    },
  });
}

/* ── Negotiation flow (journey → contract) ─────────────────────────────── */

/** `GET /contracts/negotiations/customers/journeys/:jid` — negotiations for a journey. */
export function useJourneyNegotiations(journeyId: number) {
  return useQuery({
    queryKey: journeyKeys.negotiations(journeyId),
    enabled: !!journeyId,
    queryFn: async (): Promise<ResponseArray<JourneyNegotiation>> => {
      const res = await dataCentralApi.get<ResponseArray<JourneyNegotiation>>(
        `/contracts/negotiations/customers/journeys/${journeyId}`,
      );
      return res.data;
    },
  });
}

/** `GET /contracts/negotiations/:id/histories` — negotiation history log. */
export function useNegotiationHistories(negotiationId: number) {
  return useQuery({
    queryKey: journeyKeys.negotiationHistories(negotiationId),
    enabled: !!negotiationId,
    queryFn: async (): Promise<ResponseArray<NegotiationHistory>> => {
      const res = await dataCentralApi.get<ResponseArray<NegotiationHistory>>(
        `/contracts/negotiations/${negotiationId}/histories`,
      );
      return res.data;
    },
  });
}

/**
 * Full create-negotiation body (mirrors the legacy CreateNegotiation form). The
 * journey link travels under `contract_negotiation_customer_journey_id`.
 */
export interface CreateNegotiationInput {
  contract_negotiation_title: string;
  contract_negotiation_type: string;
  contract_negotiation_content?: string;
  contract_negotiation_status?: string;
  contract_negotiation_priority?: string;
  contract_negotiation_deadline?: string;
  contract_negotiation_assignees?: number[];
  contract_negotiation_customer_id: number;
  contract_negotiation_stage?: string;
  contract_negotiation_notes?: string;
  contract_negotiation_value?: number;
  contract_negotiation_progress?: number;
  contract_negotiation_customer_journey_id: number;
  contract_negotiation_customer_journey_step?: string | null;
  attachments?: AttachmentPayload[];
}

/** `POST /contracts/negotiations` — start a price negotiation from a journey. */
export function useCreateNegotiation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateNegotiationInput): Promise<ResponseObject<JourneyNegotiation>> => {
      const res = await dataCentralApi.post<ResponseObject<JourneyNegotiation>>('/contracts/negotiations', payload);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: journeyKeys.negotiations(vars.contract_negotiation_customer_journey_id),
      });
    },
  });
}

/** `POST /contracts/negotiations/:id/resolve` — accept a negotiation. */
export function useResolveNegotiation(negotiationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: { resolution_notes: string; attachments?: unknown[] },
    ): Promise<ResponseObject<JourneyNegotiation>> => {
      const res = await dataCentralApi.post<ResponseObject<JourneyNegotiation>>(
        `/contracts/negotiations/${negotiationId}/resolve`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.negotiationHistories(negotiationId) });
    },
  });
}

/** `POST /contracts/negotiations/:id/reject` — reject a negotiation. */
export function useRejectNegotiation(negotiationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: { reason: string; notes?: string; attachments?: unknown[] },
    ): Promise<ResponseObject<JourneyNegotiation>> => {
      const res = await dataCentralApi.post<ResponseObject<JourneyNegotiation>>(
        `/contracts/negotiations/${negotiationId}/reject`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.negotiationHistories(negotiationId) });
    },
  });
}

/**
 * `POST /contracts/negotiations/:id/history/cancel` — cancel a negotiation.
 * Cancel is recorded through the history sub-resource, not a top-level verb.
 */
export function useCancelNegotiation(negotiationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: { reason: string; notes?: string; attachments?: unknown[] },
    ): Promise<ResponseObject<JourneyNegotiation>> => {
      const res = await dataCentralApi.post<ResponseObject<JourneyNegotiation>>(
        `/contracts/negotiations/${negotiationId}/history/cancel`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.negotiationHistories(negotiationId) });
    },
  });
}

/** `POST /contracts/negotiations/:id/archive` — archive a completed negotiation. */
export function useArchiveNegotiation(negotiationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ResponseObject<JourneyNegotiation>> => {
      const res = await dataCentralApi.post<ResponseObject<JourneyNegotiation>>(
        `/contracts/negotiations/${negotiationId}/archive`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.negotiationHistories(negotiationId) });
    },
  });
}

/**
 * `POST /contracts/negotiations/:id/convert` — convert a completed negotiation
 * into a CONTRACT. Legacy sends `{ notes }`.
 */
export function useConvertNegotiationToContract(negotiationId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: { notes?: string; attachments?: unknown[] } = {},
    ): Promise<ResponseObject<{ contract_id?: number; [key: string]: unknown }>> => {
      const res = await dataCentralApi.post<ResponseObject<{ contract_id?: number }>>(
        `/contracts/negotiations/${negotiationId}/convert`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: journeyKeys.negotiationHistories(negotiationId) });
    },
  });
}
